// Guest names, folder names, file names and the file allowlist (blueprint §5.3, §5.5).

// Characters Google Drive, Windows or rclone choke on, plus control characters.
const FORBIDDEN = /[/\\:*?"<>|\u0000-\u001f\u007f]/g;
const TAG_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

export const MAX_NAME_LENGTH = 60;
const MAX_FILE_NAME_LENGTH = 120;

/** Cleans a guest's name, or returns null if nothing usable is left. */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const name = raw.normalize("NFC").replace(FORBIDDEN, "").replace(/\s+/g, " ").trim();
  return name.length >= 1 && name.length <= MAX_NAME_LENGTH ? name : null;
}

export function folderTag(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  return Array.from(bytes, (b) => TAG_ALPHABET[b % TAG_ALPHABET.length]).join("");
}

export function folderName(name: string, tag: string): string {
  return `${name} (${tag})`;
}

/** The X-File-Name header, decoded and cleaned for use in an R2 key. Keeps the extension. */
export function safeFileName(header: string | undefined): string {
  let raw = "";
  try {
    raw = decodeURIComponent(header ?? "");
  } catch {
    raw = header ?? "";
  }
  raw = raw.split(/[/\\]/).pop() ?? "";
  let name = raw.normalize("NFC").replace(FORBIDDEN, "").replace(/\s+/g, " ").trim().replace(/^\.+/, "");
  if (!name) name = "photo";
  if (name.length <= MAX_FILE_NAME_LENGTH) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 && name.length - dot <= 10 ? name.slice(dot) : "";
  return name.slice(0, MAX_FILE_NAME_LENGTH - ext.length) + ext;
}

const ALLOWED_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "heic", "heif", "webp", "avif", "dng",
]);

const ALLOWED_TYPES = new Set([
  "image/jpeg", "image/png",
  "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence",
  "image/webp", "image/avif",
  "image/x-adobe-dng", "image/dng",
]);

/** D11: accepted if either the extension or the declared type is on the list. */
export function isAllowedPhoto(fileName: string, contentType: string | undefined): boolean {
  const ext = fileName.includes(".") ? fileName.split(".").pop()!.toLowerCase() : "";
  const type = (contentType ?? "").split(";")[0].trim().toLowerCase();
  return ALLOWED_EXTENSIONS.has(ext) || ALLOWED_TYPES.has(type);
}

export type NameOrder = "groom_first" | "bride_first";

/** The couple's names in display order (D29). Shared with both pages. */
export const coupleNames = (bride: string, groom: string, order: NameOrder): [string, string] =>
  order === "bride_first" ? [bride, groom] : [groom, bride];
